import { useTranslation } from "react-i18next";
import type { MedicationManagementReport, ReportAudience } from "@dosa/types";
import { Badge, Card, CardContent, CardHeader, CardTitle, MrpCategoryBadge, MrpSeverityBadge } from "@dosa/ui";

/**
 * Renders a DOSA Medication Management Report in one of two audiences:
 *  - CLINICAL: full detail, MRP classification taxonomy, pharmacist license reference —
 *    for the pharmacist record.
 *  - PATIENT_FRIENDLY: plain-language framing, no internal classification jargon — for
 *    the patient to read and act on. See docs/CLINICAL_WORKFLOW.md.
 * Both views are built from the same underlying MedicationManagementReport data; nothing is
 * fabricated per-audience, only presentation and terminology are adapted.
 */
export function MedicationReportView({ report, audience }: { report: MedicationManagementReport; audience: ReportAudience }) {
  const { t } = useTranslation();
  const isClinical = audience === "CLINICAL";

  return (
    <div className="space-y-6 print:space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{isClinical ? "DOSA Medication Management Report — Clinical" : "تقرير إدارة الأدوية من دوزا"}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 text-sm sm:grid-cols-2">
          <div>
            <p className="text-charcoal-400">{isClinical ? "Patient" : "المريض"}</p>
            <p className="font-medium text-charcoal-800">
              {report.patient.firstName} {report.patient.lastName} · {report.patient.patientCode}
            </p>
          </div>
          <div>
            <p className="text-charcoal-400">{isClinical ? "Reviewing pharmacist" : "الصيدلي المسؤول"}</p>
            <p className="font-medium text-charcoal-800">
              {report.pharmacist.firstName} {report.pharmacist.lastName}
              {isClinical && ` · ${report.pharmacist.licenseNumber}`}
            </p>
          </div>
          <div>
            <p className="text-charcoal-400">{isClinical ? "Assessment type" : "نوع المراجعة"}</p>
            <p className="font-medium text-charcoal-800">{report.assessment.type.replaceAll("_", " ")}</p>
          </div>
          <div>
            <p className="text-charcoal-400">{isClinical ? "Generated" : "تاريخ الإنشاء"}</p>
            <p className="font-medium text-charcoal-800">{new Date(report.generatedAt).toLocaleDateString()}</p>
          </div>
        </CardContent>
      </Card>

      {report.assessment.clinicalSummary && (
        <Card>
          <CardHeader>
            <CardTitle>{isClinical ? "Clinical Summary" : "ملخص الحالة"}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-charcoal-700">{report.assessment.clinicalSummary}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{isClinical ? "Medication List" : "قائمة الأدوية"}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ul className="divide-y divide-charcoal-100">
            {report.medications.map((m) => (
              <li key={m.id} className="flex items-center justify-between px-5 py-3 text-sm">
                <div>
                  <p className="font-medium text-charcoal-800">{m.freeTextName}</p>
                  <p className="text-charcoal-400">{[m.dose, m.frequency?.replaceAll("_", " ")].filter(Boolean).join(" · ")}</p>
                </div>
                <Badge variant={m.status === "ACTIVE" ? "success" : "neutral"}>{m.status}</Badge>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {report.medicationRelatedProblems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{isClinical ? "Medication-Related Problems" : "المشاكل المتعلقة بالأدوية"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {report.medicationRelatedProblems.map((p) => (
              <div key={p.id} className="rounded-md border border-charcoal-100 p-3">
                {isClinical && (
                  <div className="mb-2 flex gap-2">
                    <MrpCategoryBadge category={p.category} label={p.category} />
                    <MrpSeverityBadge severity={p.severity} label={p.severity} />
                  </div>
                )}
                <p className="text-sm text-charcoal-700">{isClinical ? p.description : p.descriptionAr || p.description}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {isClinical && report.interventions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Pharmacist Interventions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {report.interventions.map((i) => (
              <div key={i.id} className="rounded-md border border-charcoal-100 p-3 text-sm">
                <p className="font-medium text-charcoal-800">{i.type.replaceAll("_", " ")}</p>
                <p className="mt-1 text-charcoal-600">{i.recommendation}</p>
                {i.rationale && <p className="mt-1 text-charcoal-400">Rationale: {i.rationale}</p>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {report.monitoringParameters.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{isClinical ? "Monitoring Plan" : "خطة المراقبة"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {report.monitoringParameters.map((mp) => (
              <div key={mp.id} className="flex items-center justify-between text-sm">
                <span className="text-charcoal-700">{isClinical ? mp.parameter : mp.parameterAr || mp.parameter}</span>
                <span className="text-charcoal-400">{mp.frequency}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {report.actionItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{isClinical ? "Medication Action Plan" : t("patient.followUp.title") + " — " + "خطة العمل الدوائية"}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-inside list-disc space-y-1.5 text-sm text-charcoal-700">
              {report.actionItems.map((item) => (
                <li key={item.id}>{isClinical ? item.instruction : item.instructionAr || item.instruction}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {report.followUp && (
        <Card>
          <CardHeader>
            <CardTitle>{isClinical ? "Follow-up Plan" : "خطة المتابعة"}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-charcoal-700">
              {t("patient.followUp.dueOn", { date: new Date(report.followUp.dueDate).toLocaleDateString() })}
            </p>
            {report.followUp.reason && <p className="mt-1 text-sm text-charcoal-400">{report.followUp.reason}</p>}
          </CardContent>
        </Card>
      )}

      {!isClinical && (
        <p className="text-xs text-charcoal-400">
          This report was prepared by a licensed pharmacist. It does not replace advice from your doctor — always contact your
          care team with questions about your treatment.
        </p>
      )}
    </div>
  );
}
